<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { useRoute } from 'vue-router'
import ProjectFilters from './ProjectFilters.vue'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Save, Download, Filter, ChevronDown, ChevronUp } from 'lucide-vue-next'
import LayerFeaturesList from '@/components/layers/variants/LayerFeaturesList.vue'
import AIReporting from '@/components/layers/AIReporting.vue'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const isUpdating = ref(false)
const isDownloading = ref(false)
const currentFilters = ref(null)
const showFilters = ref(false)
const hasActiveFilters = ref(false)

// Computed property for filter config to simplify deep property access
const layerFilterConfig = computed(() => {
  return props.layer?.properties?.layer?.data?.filter?.config
})

const layerCode = computed(() => {
  return props.layer?.uuid
})

const fetchFeaturesWithFilters = async () => {
  try {
    const selectedRange = store.getters['layers/getSelectedRange']

    if (!selectedRange || selectedRange.length < 2) {
      return
    }

    const [startDate, endDate] = selectedRange

    let url = `${HOST}/org/${orgId.value}/layers/${layerCode.value}/features`
    url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`

    const response = await API({
      method: 'GET',
      url: url
    })

    if (response.data?.type === 'FeatureCollection' && Array.isArray(response.data.features)) {
      await store.dispatch('layers/updateOrgLayerFeatures', {
        orgId: orgId.value,
        layerId: layerCode.value,
        features: response.data.features
      })
    }
  } catch (error) {
    console.error('Error fetching features:', error)
  }
}

const downloadCSV = async () => {
  if (!layerCode.value) return

  isDownloading.value = true
  try {
    // Get current date range from the store
    const selectedRange = store.getters['layers/getSelectedRange']

    let url = `${HOST}/org/${orgId.value}/layers/${layerCode.value}/features/csv`
    const params = {}

    if (selectedRange && Array.isArray(selectedRange) && selectedRange.length >= 2) {
      params.startDate = selectedRange[0]
      params.endDate = selectedRange[1]
    }

    const response = await API({
      method: 'GET',
      url: url,
      params: params,
      responseType: 'blob' // Important for file downloads
    })

    // Get filename from Content-Disposition header or create default
    let filename = 'features_export.csv'
    if (response.headers && response.headers['content-disposition']) {
      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    // If no filename in headers, create one with timestamp
    if (filename === 'features_export.csv') {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')
      filename = `features_export_${timestamp}.csv`
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: 'text/csv' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)

    toast({
      title: 'Download successful',
      description: `CSV file ${filename} downloaded successfully`,
      variant: 'default'
    })

  } catch (error) {
    console.error('Error downloading CSV:', error)
    toast({
      title: 'Download failed',
      description: 'Failed to download CSV file',
      variant: 'destructive'
    })
  } finally {
    isDownloading.value = false
  }
}



const saveFilterChanges = async (filterData) => {
  if (!props.layer || props.layer.properties.type !== 4) return

  isUpdating.value = true
  try {
    const filterConfig = {
      type: "AndFilter",
      config: []
    };

    if (filterData.projectIds && filterData.projectIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "project_id",
        config: filterData.projectIds.map(id => id.toString())
      });
    }

    if (filterData.templateIds && filterData.templateIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "template_id",
        config: filterData.templateIds.map(id => id.toString())
      });
    }

    if (filterData.memberIds && filterData.memberIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "member_id",
        config: filterData.memberIds.map(id => id.toString())
      });
    }

    if (filterData.searchText) {
      filterConfig.config.push({
        type: "TextFilter",
        field_name: "search",
        config: filterData.searchText
      });
    }

    if (filterData.isPublicData !== undefined) {
      filterConfig.config.push({
        type: "BoolFilter",
        field_name: "is_public",
        config: filterData.isPublicData
      });
    }

    if (filterData.dateRange && filterData.dateRange.start && filterData.dateRange.end) {
      filterConfig.config.push({
        type: "DateRangeFilter",
        field_name: "acquired",
        config: {
          gte: filterData.dateRange.start.toISOString(),
          lte: filterData.dateRange.end.toISOString()
        }
      });
    }

    if (filterData.geometry) {
      filterConfig.config.push({
        type: "GeometryFilter",
        field_name: "geometry",
        config: filterData.geometry.geometry
      });
    }

    if (filterData.geometryTypes && filterData.geometryTypes.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "geometry_type",
        config: filterData.geometryTypes
      });
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId.value}/layers/${layerId.value}/collected-data`,
      data: {
        filter: filterConfig
      }
    })

    // Update the layer in the store with the new filter
    const updatedLayer = {
      ...props.layer,
      properties: {
        ...props.layer.properties,
        layer: {
          ...props.layer.properties.layer,
          data: {
            filter: filterConfig
          }
        }
      }
    }

    await store.dispatch('layers/updateOrgLayer', {
      orgId: orgId.value,
      layer: updatedLayer
    })

    // Fetch fresh features with the new filters
    await fetchFeaturesWithFilters()

    // Features list will update automatically via Vue reactivity

    toast({
      title: 'Filters saved',
      description: 'Filter settings have been applied successfully',
      variant: 'default'
    })

  } catch (error) {
    console.error('Error saving filters:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to save filter settings',
      variant: 'destructive'
    });
  } finally {
    isUpdating.value = false;
  }
}

// Helper function to check for non-empty arrays
function hasNonEmptyArray(arr) {
  return Array.isArray(arr) && arr.length > 0
}

const updateFilters = (filters) => {
  currentFilters.value = filters

  // Check if any filters are active
  const hasFilters =
    hasNonEmptyArray(filters.projectIds) ||
    hasNonEmptyArray(filters.templateIds) ||
    hasNonEmptyArray(filters.memberIds) ||
    (filters.searchText && filters.searchText.trim() !== '') ||
    filters.isPublicData ||
    filters.geometry != null ||
    hasNonEmptyArray(filters.geometryTypes)

  hasActiveFilters.value = hasFilters

  // Auto-show filters if user has modified them
  if (hasFilters) {
    showFilters.value = true
  }
}

// Initialize filters visibility based on existing filters
watch(layerFilterConfig, (filterConfig) => {
  if (filterConfig && filterConfig.length > 0) {
    hasActiveFilters.value = true
    showFilters.value = true
  }
}, { immediate: true })
</script>

<template>
  <div>
    <!-- Data Tab Content -->
    <div v-if="activeTab === 'data'">
      <!-- Top Action Bar -->
      <div class="flex justify-between items-center gap-3 mb-4">
        <div class="flex items-center gap-2">
          <Button
            @click="showFilters = !showFilters"
            variant="ghost"
            size="sm"
            class="text-primary hover:bg-primary/10 relative"
          >
            <Filter class="h-4 w-4 mr-2" />
            Filters
            <span v-if="hasActiveFilters" class="ml-1 w-2 h-2 bg-primary rounded-full inline-block"></span>
            <component :is="showFilters ? ChevronUp : ChevronDown" class="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      <!-- Filters Section (Collapsible) -->
      <div
        v-if="showFilters"
        class="border rounded-lg p-4 mb-4 bg-gray-50/50"
      >
        <ProjectFilters
          v-if="layer?.properties?.layer?.data"
          :initial-filter="layer?.properties?.layer?.data"
          :org-id="orgId"
          @update:filters="updateFilters"
        />

        <div class="flex justify-end mt-4">
          <Button
            @click="saveFilterChanges(currentFilters)"
            :disabled="isUpdating"
            size="sm"
            class="bg-primary text-white hover:bg-primary/90"
          >
            <Save class="h-4 w-4 mr-2" />
            <span v-if="isUpdating">Saving...</span>
            <span v-else>Save</span>
          </Button>
        </div>
      </div>

      <!-- Data List -->
      <LayerFeaturesList :code="layerCode" />

      <!-- Download CSV Button -->
      <div class="flex justify-center mt-4">
        <Button
          @click="downloadCSV"
          :disabled="isDownloading"
          variant="outline"
          size="sm"
          class="border-primary text-primary hover:bg-primary hover:text-white"
        >
          <Download class="h-4 w-4 mr-2" />
          <span v-if="isDownloading">Downloading...</span>
          <span v-else>Download CSV</span>
        </Button>
      </div>
    </div>

    <!-- AI Reports Tab Content -->
    <div v-if="activeTab === 'ai-reports'">
      <AIReporting :layer="layer" />
    </div>
  </div>
</template>
